import React, { useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Upload, X, File as FileIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FileUploadFile {
  file: File;
  id: string;
  error?: string;
}

export interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  /** Max file size in bytes */
  maxSize?: number;
  onChange?: (files: File[]) => void;
  className?: string;
  disabled?: boolean;
}

/** Imperative handle exposing a way to add files from outside (e.g. clipboard paste). */
export interface FileUploadHandle {
  addFiles: (files: FileList | File[]) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

let counter = 0;
function uid() {
  return `fu-${++counter}-${Date.now()}`;
}

/** Extract image files from a paste event's clipboard data. */
export function getImageFilesFromClipboard(clipboardData: DataTransfer | null): File[] {
  if (!clipboardData) return [];
  return Array.from(clipboardData.items)
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FileUpload = forwardRef<FileUploadHandle, FileUploadProps>(function FileUpload({
  accept,
  multiple = false,
  maxSize,
  onChange,
  className,
  disabled = false,
}, ref) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileUploadFile[]>([]);
  const [dragging, setDragging] = useState(false);

  const processFiles = useCallback(
    (incoming: FileList | File[]) => {
      const incomingArr = Array.from(incoming);

      const processed: FileUploadFile[] = incomingArr.map((file) => ({
        file,
        id: uid(),
        error:
          maxSize && file.size > maxSize
            ? `File exceeds ${formatBytes(maxSize)} limit`
            : undefined,
      }));

      const next = multiple ? [...files, ...processed] : processed.slice(0, 1);
      setFiles(next);
      onChange?.(next.filter((f) => !f.error).map((f) => f.file));
    },
    [files, maxSize, multiple, onChange],
  );

  useImperativeHandle(ref, () => ({ addFiles: processFiles }), [processFiles]);

  function handleRemove(id: string) {
    const next = files.filter((f) => f.id !== id);
    setFiles(next);
    onChange?.(next.filter((f) => !f.error).map((f) => f.file));
  }

  // ─── Drag events ─────────────────────────────────────────────────────

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!disabled) setDragging(true);
  }

  function handleDragLeave() {
    setDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    if (e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      // Reset input value so the same file can be re-selected
      e.target.value = '';
    }
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-2',
          'rounded-lg border-2 border-dashed px-6 py-8 text-center',
          'transition-colors duration-150 cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          dragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <Upload className="h-7 w-7 text-slate-400" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-slate-700">
            Drop files here or{' '}
            <span className="text-blue-600">click to browse</span>
          </p>
          {(accept || maxSize) && (
            <p className="text-xs text-slate-400 mt-0.5">
              {[accept && `Accepted: ${accept}`, maxSize && `Max size: ${formatBytes(maxSize)}`]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
      </div>

      {/* Hidden input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={handleInputChange}
        className="sr-only"
        aria-hidden="true"
      />

      {/* File list */}
      {files.length > 0 && (
        <ul className="space-y-2" aria-label="Selected files">
          {files.map((f) => (
            <li
              key={f.id}
              className={cn(
                'flex items-center gap-3 rounded-md border px-3 py-2',
                f.error ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white',
              )}
            >
              {f.error ? (
                <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500" />
              ) : (
                <FileIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
              )}

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{f.file.name}</p>
                <p className={cn('text-xs', f.error ? 'text-red-600' : 'text-slate-400')}>
                  {f.error ?? formatBytes(f.file.size)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => handleRemove(f.id)}
                aria-label={`Remove ${f.file.name}`}
                className="flex-shrink-0 inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
